import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import Icon from './icons/Icon';
import * as vfsClient from '../utils/vfsClient';
import {buildPlan, CONFLICT_POLICIES, formatBytes, joinPath, sendingDetail, summarizeResults, uploadPercent} from '../utils/treeUpload/uploadPlan';
import {fetchServerLimits} from '../utils/treeUpload/serverLimits';
import {runUpload} from '../utils/treeUpload/runUpload';
import './TreeUploadDialog.css';

const EXCLUDED_LABELS = {
  junk: 'system junk (.DS_Store, Thumbs.db, __MACOSX)',
  'skipped-folder': 'inside node_modules or .git',
  reserved: 'inside .git (never uploaded: it would clash with the project history)',
  unsafe: 'unsafe paths',
};

const STATUS_LABELS = {
  queued: 'Waiting',
  packing: 'Reading',
  packed: 'Packed',
  sending: 'Sending',
  created: 'Added',
  overwritten: 'Overwritten',
  renamed: 'Kept both',
  skipped: 'Skipped',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const DONE_STATES = new Set(['created', 'overwritten', 'renamed', 'skipped', 'failed', 'cancelled']);

/** What the policy does to one existing file, for the per-file list. */
const conflictOutcome = (policy) => ({overwrite: 'will be overwritten', skip: 'will be skipped', rename: 'kept, the new one renamed'}[policy]);

/**
 * Review → upload → report for files and folders going into the file tree. `collected` is what was dropped or
 * picked ({files: [{file, relPath, size}], emptyFolders, unreadable}); `target` the tree folder ('' = the tree's root).
 * `toVfsPath(treePath)` maps a tree path to the project path. `onFinished({results, bytesByPath, targetFolder})` runs
 * after every upload attempt that sent something (the tree refreshes and selects the new files).
 */
const TreeUploadDialog = ({projectId, collected, target, folderChoices, rootLabel, toVfsPath, onClose, onFinished}) => {
  const [targetFolder, setTargetFolder] = useState(target || '');
  const [skipHeavy, setSkipHeavy] = useState(true);
  const [policy, setPolicy] = useState('skip');
  const [existing, setExisting] = useState(new Set());
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState(null);
  const [phase, setPhase] = useState('review'); // review | uploading | done
  const [progress, setProgress] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [onlyRelPaths, setOnlyRelPaths] = useState(null); // a retry: just these files
  const abortRef = useRef(null);
  // What the project's sandbox takes (archive size, files, free disk): the plan and the parts follow it.
  const [limits, setLimits] = useState(undefined);
  useEffect(() => {
    let cancelled = false;
    fetchServerLimits({projectId}).then((answer) => { if (!cancelled) { setLimits(answer); } });
    return () => { cancelled = true; };
  }, [projectId]);

  const plan = useMemo(() => {
    const entries = onlyRelPaths ? collected.files.filter((entry) => onlyRelPaths.has(entry.relPath)) : collected.files;
    return buildPlan(entries, {targetFolder, skipHeavy, existing, emptyFolders: onlyRelPaths ? [] : collected.emptyFolders, ...(limits ? {limits} : {})});
  }, [collected, targetFolder, skipHeavy, existing, onlyRelPaths, limits]);

  const destKey = plan.files.map((file) => file.destPath).join('\n');
  // Which destination files already exist (one POST /stat-many per 500 paths).
  useEffect(() => {
    if (phase !== 'review' || !plan.files.length) { return undefined; }
    let cancelled = false;
    setChecking(true);
    setCheckError(null);
    const timer = setTimeout(async () => {
      try {
        const byVfsPath = new Map(plan.files.map((file) => [toVfsPath(file.destPath), file.destPath]));
        const found = await vfsClient.existingPaths(projectId, [...byVfsPath.keys()]);
        if (!cancelled) { setExisting(new Set([...found].map((path) => byVfsPath.get(path)))); }
      } catch (error) {
        if (!cancelled) { setCheckError('Could not check which files already exist; the conflict choice still applies.'); }
      } finally {
        if (!cancelled) { setChecking(false); }
      }
    }, 150);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destKey, phase, projectId]);

  const excludedByReason = plan.excluded.reduce((counts, entry) => ({...counts, [entry.reason]: (counts[entry.reason] || 0) + 1}), {});
  const targetLabel = targetFolder ? `${rootLabel ? `${rootLabel}/` : ''}${targetFolder}` : (rootLabel || '/ (root)');
  const canUpload = phase === 'review' && plan.files.length > 0 && plan.problems.length === 0 && !checking && limits !== undefined;

  const start = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase('uploading');
    setProgress({phase: 'packing', results: {}, batch: 0, batches: 1});
    const result = await runUpload({
      projectId,
      vfsTarget: toVfsPath(targetFolder),
      files: plan.files,
      conflict: policy,
      signal: controller.signal,
      onProgress: setProgress,
      ...(limits ? {limits} : {}),
    });
    abortRef.current = null;
    setOutcome(result);
    setPhase('done');
    const sent = Object.values(result.results).some((line) => ['created', 'overwritten', 'renamed'].includes(line.status));
    if (sent || result.serverMayHaveSaved) {
      onFinished?.({results: result.results, bytesByPath: result.bytesByPath, targetFolder, files: plan.files});
    }
  };

  const retryFailed = () => {
    const failed = Object.entries(outcome?.results || {}).filter(([, line]) => line.status === 'failed' || line.status === 'cancelled').map(([relPath]) => relPath);
    setOnlyRelPaths(new Set(failed));
    setOutcome(null);
    setProgress(null);
    setExisting(new Set());
    setPhase('review');
  };

  const results = (phase === 'done' ? outcome?.results : progress?.results) || {};
  const finished = Object.values(results).filter((line) => DONE_STATES.has(line.status) || line.status === 'packed' || line.status === 'sending').length;
  const percent = phase === 'done' ? (plan.files.length ? Math.round((finished / plan.files.length) * 100) : 0) : uploadPercent(progress, finished, plan.files.length);
  const counts = summarizeResults(Object.values(outcome?.results || {}));
  const retryable = counts.failed + counts.cancelled;

  const title = phase === 'done' ? 'Upload finished' : phase === 'uploading' ? 'Uploading…' : (onlyRelPaths ? 'Retry upload' : 'Upload to the project');

  return (
    <Modal
      className="tree-upload-modal"
      isOpen
      onClose={phase === 'uploading' ? null : onClose}
      title={title}
      type="custom"
    >
      <div className="tree-upload" data-testid="tree-upload-dialog">
        {phase === 'review' && (
          <>
            <div className="tree-upload__summary" data-testid="tree-upload-summary">
              <strong>{plan.files.length}</strong> file{plan.files.length === 1 ? '' : 's'}
              {plan.folderCount > 0 && <>, <strong>{plan.folderCount}</strong> folder{plan.folderCount === 1 ? '' : 's'}</>}
              {' · '}{formatBytes(plan.totalBytes)}
            </div>
            <label className="tree-upload__label" htmlFor="tree-upload-target">Upload into</label>
            <select
              className="tree-upload__select"
              id="tree-upload-target"
              onChange={(event) => { setExisting(new Set()); setTargetFolder(event.target.value); }}
              value={targetFolder}
            >
              {[...new Set(['', targetFolder, ...folderChoices])].map((path) => (
                <option key={path || '/'} value={path}>{path ? joinPath(rootLabel, path) : (rootLabel || '/ (root)')}</option>
              ))}
            </select>

            <label className="tree-upload__toggle">
              <input checked={skipHeavy} onChange={(event) => setSkipHeavy(event.target.checked)} type="checkbox" />
              <span className="tree-upload__toggle-text">Skip node_modules and .git</span>
            </label>

            {plan.excluded.length > 0 && (
              <ul className="tree-upload__notes" data-testid="tree-upload-excluded">
                {Object.entries(excludedByReason).map(([reason, count]) => (
                  <li key={reason}>{count} left out: {EXCLUDED_LABELS[reason] || reason}</li>
                ))}
              </ul>
            )}
            {collected.unreadable?.length > 0 && (
              <ul className="tree-upload__notes tree-upload__notes--warn">
                {collected.unreadable.slice(0, 3).map((entry) => <li key={entry.relPath}>{entry.relPath}: {entry.error}</li>)}
              </ul>
            )}

            {checking && <div className="tree-upload__muted">Checking for files that already exist…</div>}
            {checkError && <div className="tree-upload__muted">{checkError}</div>}
            {!checking && plan.conflicts.length > 0 && (
              <div className="tree-upload__conflicts" data-testid="tree-upload-conflicts">
                <div className="tree-upload__conflicts-title">
                  <Icon name="warning" size={14} />
                  <span>{plan.conflicts.length} file{plan.conflicts.length === 1 ? '' : 's'} already exist{plan.conflicts.length === 1 ? 's' : ''}</span>
                </div>
                <div className="tree-upload__policies" role="radiogroup" aria-label="When a file already exists">
                  {CONFLICT_POLICIES.map((option) => (
                    <label className={`tree-upload__policy ${policy === option.value ? 'is-active' : ''}`} key={option.value}>
                      <input checked={policy === option.value} name="tree-upload-policy" onChange={() => setPolicy(option.value)} type="radio" value={option.value} />
                      <span className="tree-upload__policy-text">{option.label}</span>
                    </label>
                  ))}
                </div>
                <ul className="tree-upload__files">
                  {plan.conflicts.slice(0, 200).map((destPath) => (
                    <li key={destPath}>
                      <span className="tree-upload__path">{destPath}</span>
                      <span className="tree-upload__state">{conflictOutcome(policy)}</span>
                    </li>
                  ))}
                  {plan.conflicts.length > 200 && <li className="tree-upload__muted">… and {plan.conflicts.length - 200} more</li>}
                </ul>
              </div>
            )}

            {plan.problems.map((problem) => <div className="tree-upload__problem" key={problem} role="alert">{problem}</div>)}
            {plan.warnings.map((warning) => <div className="tree-upload__warning" key={warning}>{warning}</div>)}
            {plan.files.length === 0 && <div className="tree-upload__problem" role="alert">Nothing to upload: every file was left out.</div>}

            <div className="tree-upload__target-line">Destination: <span className="tree-upload__path">{targetLabel}</span></div>
            <div className="tree-upload__actions">
              <button className="tree-upload__btn" onClick={onClose} type="button">Cancel</button>
              <button className="tree-upload__btn tree-upload__btn--primary" data-testid="tree-upload-start" disabled={!canUpload} onClick={start} type="button">
                Upload {plan.files.length} file{plan.files.length === 1 ? '' : 's'}
              </button>
            </div>
          </>
        )}

        {phase !== 'review' && (
          <>
            {phase === 'uploading' && (
              <div className="tree-upload__muted">
                {progress?.phase === 'sending' ? 'Sending to the project…' : 'Reading and packing files…'}
                {sendingDetail(progress) ? ` (${sendingDetail(progress)})` : (progress?.batches > 1 ? ` (part ${progress.batch + 1} of ${progress.batches})` : '')}
              </div>
            )}
            {phase === 'done' && (
              <div className="tree-upload__summary" data-testid="tree-upload-report">
                {counts.created + counts.overwritten + counts.renamed} uploaded to <span className="tree-upload__path">{targetLabel}</span>
                {counts.overwritten > 0 && ` · ${counts.overwritten} overwritten`}
                {counts.renamed > 0 && ` · ${counts.renamed} kept both`}
                {counts.skipped > 0 && ` · ${counts.skipped} skipped`}
                {counts.failed > 0 && ` · ${counts.failed} failed`}
                {counts.cancelled > 0 && ` · ${counts.cancelled} cancelled`}
              </div>
            )}
            {phase === 'done' && outcome?.serverMayHaveSaved && (
              <div className="tree-upload__warning">Cancelled while sending: the project may have received part of it. The tree shows what arrived.</div>
            )}
            <div className="tree-upload__bar" aria-valuemax={100} aria-valuemin={0} aria-valuenow={percent} role="progressbar">
              <div className={`tree-upload__bar-fill ${phase === 'uploading' && progress?.phase === 'sending' ? 'is-busy' : ''}`} style={{width: `${percent}%`}} />
            </div>
            <ul className="tree-upload__files tree-upload__files--progress" data-testid="tree-upload-files">
              {plan.files.slice(0, 500).map((file) => {
                const line = results[file.relPath] || {status: 'queued'};
                return (
                  <li className={`is-${line.status}`} key={file.relPath}>
                    <span className="tree-upload__path" title={file.destPath}>{line.path && line.path !== file.relPath ? `${file.relPath} → ${line.path}` : file.relPath}</span>
                    <span className="tree-upload__state">{STATUS_LABELS[line.status] || line.status}{line.error ? `: ${line.error}` : ''}</span>
                  </li>
                );
              })}
              {plan.files.length > 500 && <li className="tree-upload__muted">… and {plan.files.length - 500} more</li>}
            </ul>
            <div className="tree-upload__actions">
              {phase === 'uploading' && (
                <button className="tree-upload__btn" data-testid="tree-upload-cancel" onClick={() => abortRef.current?.abort()} type="button">Cancel upload</button>
              )}
              {phase === 'done' && retryable > 0 && (
                <button className="tree-upload__btn" data-testid="tree-upload-retry" onClick={retryFailed} type="button">Retry {retryable} file{retryable === 1 ? '' : 's'}</button>
              )}
              {phase === 'done' && (
                <button className="tree-upload__btn tree-upload__btn--primary" onClick={onClose} type="button">Close</button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

TreeUploadDialog.propTypes = {
  collected: PropTypes.shape({
    files: PropTypes.array.isRequired,
    emptyFolders: PropTypes.array,
    unreadable: PropTypes.array,
  }).isRequired,
  folderChoices: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
  onFinished: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  rootLabel: PropTypes.string,
  target: PropTypes.string,
  toVfsPath: PropTypes.func.isRequired,
};

export default TreeUploadDialog;
